import { useSnackbar } from 'notistack';
import React from 'react';
import * as Yup from 'yup';
import { ChargingProfilePurposeType, useCommandClearChargingProfile } from '../../../../../../../../app/ApiGen';
import { Dialog, DialogProps } from '../../../../../../../../components/Dialog';
import { SelectionMenu } from '../../../../../../../../components/SelectionMenu';
import { Form, FormField, OnSubmit } from '../../../../../../../../design-system';
import { createSchema } from '../../../../../../../../utils/formUtils';
import { useChargePoint } from '../../../Provider';
import { useHandleError } from './useHandleError';

const fields: FormField[] = [
  { id: 'id', label: 'Profile ID' },
  { id: 'connectorId', label: 'Connector ID' },
  {
    id: 'chargingProfilePurpose',
    label: 'Profile Purpose',
    inputComponent: ({ disabled, error, name, onBlur, onChange }) => (
      <SelectionMenu
        disabled={disabled}
        error={error}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        options={[{ value: 'ChargePointMaxProfile' }, { value: 'TxDefaultProfile' }, { value: 'TxProfile' }]}
      />
    ),
  },
  { id: 'stackLevel', label: 'Stack Level' },
];

const initialValues = {
  id: undefined,
  connectorId: undefined,
  chargingProfilePurpose: undefined,
  stackLevel: undefined,
};

type ClearChargingProfileDialogFields = {
  id?: number;
  connectorId?: number;
  chargingProfilePurpose?: ChargingProfilePurposeType;
  stackLevel?: number;
};

const validationSchema = createSchema<ClearChargingProfileDialogFields>({
  id: Yup.number().optional(),
  connectorId: Yup.number().optional(),
  chargingProfilePurpose: Yup.string<ChargingProfilePurposeType>().optional(),
  stackLevel: Yup.number().optional(),
});

export const ClearChargingProfileDialog: React.FC<DialogProps> = ({ isOpen, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();

  const { chargePoint, refetch } = useChargePoint();

  const { error, mutate } = useCommandClearChargingProfile({ id: chargePoint?.id ?? '' });

  useHandleError({ error, defaultErrorMessage: 'Unable to clear charging profile' });

  const onSubmit = React.useCallback<OnSubmit<ClearChargingProfileDialogFields>>(
    async ({ id, connectorId, chargingProfilePurpose, stackLevel }, { setSubmitting }) => {
      try {
        const { data } = await mutate({ id, connectorId, chargingProfilePurpose, stackLevel });
        enqueueSnackbar(`Clear charging profile command ${data.status.toLocaleLowerCase()}`);
        onClose();
        refetch();
      } finally {
        setSubmitting(false);
      }
    },
    [onClose, mutate, refetch, enqueueSnackbar],
  );

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Clear charging profile">
      <Form<ClearChargingProfileDialogFields>
        fields={fields}
        initialValues={initialValues}
        onSubmit={onSubmit}
        labels={{ save: 'Send command' }}
        isEditing
        validationSchema={validationSchema}
      />
    </Dialog>
  );
};
